import { formatDate } from '@common/paymentsUtils/dateUtils';
import { IconEditFilled } from '@zillow/constellation-icons';
import { Box, IconButton, Paragraph, Text } from '@zillow/constellation-10';
import TransactionConstants from '@common/constants/TransactionConstants';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import { HiddenText } from '@common/components/HiddenText/HiddenText';
import type { RenterPaymentMethodInterface, RenterPaymentSubscriptionInterface } from '@renter-hub/models/Types';
import PontoonConstants from '@common/constants/PontoonExceptionConstants';
import PaymentMethodModule from '@renter-hub/components/PaymentMethodModule';
import { RENT_PAYMENT } from '@renter-hub/constants/paymentConstants';

interface NextPayableInterface {
  isScheduled: boolean;
}
interface Props {
  currentTransaction?: any;
  paymentSubscriptionDetails?: RenterPaymentSubscriptionInterface;
  accountType?: any;
  goToEditPaymentSchedulingPage?: any;
  goToEditPaymentFrequencyPage?: any;
  goToEditPaymentInfoPage?: (typeOfPaymentToMake?: typeof RENT_PAYMENT) => void;
  displayHiddenText?: boolean;
  allowAutoPayEdit?: boolean;
  paymentMethods: Array<RenterPaymentMethodInterface>;
  nextPayable: NextPayableInterface;
  recordedByName?: string;
}

export const PaymentDetails = (props: Props) => {
  const {
    currentTransaction = { amount: 0 },
    paymentSubscriptionDetails = {
      nextScheduledDate: '',
      firstScheduledDate: '',
      recurring: null,
      paymentMethodId: '',
    },
    goToEditPaymentSchedulingPage,
    goToEditPaymentFrequencyPage,
    displayHiddenText,
    allowAutoPayEdit,
    paymentMethods,
    goToEditPaymentInfoPage,
    nextPayable,
    recordedByName,
  } = props;

  const { dueDate, amount, zgFee, totalAmount, renterStatus, accountName, accountLast4 } = currentTransaction;
  const { nextScheduledDate, firstScheduledDate, recurring, paymentMethodId } = paymentSubscriptionDetails;

  // Send date only displays for unpaid/refunded payments, so always use subscription dates
  const sendDateValue = nextScheduledDate || firstScheduledDate;
  const sendDateExists = Boolean(sendDateValue);
  const sendDate = sendDateValue ? formatDate(sendDateValue, 'do') : '';
  const renterCanEditPayment =
    renterStatus === TransactionConstants.STATUS.UNPAID || renterStatus === TransactionConstants.STATUS.REFUNDED;
  const selectedPaymentMethod = paymentMethods?.find((paymentMethod) => paymentMethod.id === paymentMethodId);
  const isCompleted = renterStatus === TransactionConstants.STATUS.COMPLETE;
  const displayAccountName = isCompleted
    ? accountName
    : selectedPaymentMethod?.displayName || selectedPaymentMethod?.nickname;
  const displayAccountLast4 = isCompleted ? accountLast4 : selectedPaymentMethod?.accountLast4;
  const shouldShowTotalPayment =
    (nextPayable.isScheduled && selectedPaymentMethod && selectedPaymentMethod.accountType !== 'bank_account') ||
    isCompleted;

  const isUsingSelectedPaymentMethod = Boolean(selectedPaymentMethod);

  const displayTotalAmount = isUsingSelectedPaymentMethod
    ? selectedPaymentMethod?.rentAmountDetails?.totalAmount
    : totalAmount;

  const displayFee = isUsingSelectedPaymentMethod ? selectedPaymentMethod?.rentAmountDetails?.fee : zgFee;

  const totalPaymentText = displayTotalAmount
    ? `${centsToDollars(displayTotalAmount)}${
        displayFee && displayFee > 0 ? ` (${centsToDollars(displayFee)} fee)` : ''
      }`
    : null;

  return (
    <>
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: {
            base: 'repeat(2, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
        }}
      >
        <Box
          css={{
            paddingRight: 'default',
            paddingTop: 'default',
            paddingBottom: 'default',
          }}
        >
          <Box css={{ display: 'flex', alignItems: 'center', minHeight: 'action.xs' }}>
            <Paragraph textStyle="body-bold">Monthly rent</Paragraph>
          </Box>
          <Text>
            {amount ? (
              `${centsToDollars(amount, { style: 'effectiveShort' })}/mo`
            ) : (
              <HiddenText visible={displayHiddenText} />
            )}
          </Text>
        </Box>
        <Box
          css={{
            paddingRight: 'default',
            paddingTop: 'default',
            paddingBottom: 'default',
          }}
        >
          <Box css={{ display: 'flex', alignItems: 'center', minHeight: 'action.xs' }}>
            <Paragraph textStyle="body-bold">Due date</Paragraph>
          </Box>
          <Text>
            {dueDate ? (
              `${formatDate(
                dueDate,
                'do',
                PontoonConstants.RENTER_HUB.RENTER_DASHBOARD.RENT_PAYMENT.DUE_DATE,
              )} of the month`
            ) : (
              <HiddenText visible={displayHiddenText} />
            )}
          </Text>
        </Box>
        {renterCanEditPayment && sendDateExists && (
          <Box
            css={{
              paddingRight: 'default',
              paddingTop: 'default',
              paddingBottom: 'default',
            }}
          >
            <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter', minHeight: 'action.xs' }}>
              <Paragraph textStyle="body-bold">Send date</Paragraph>
              {renterCanEditPayment && (
                <IconButton
                  icon={<IconEditFilled />}
                  title="Edit send date"
                  tone="brand"
                  emphasis="bare"
                  size="xs"
                  shape="square"
                  onClick={goToEditPaymentSchedulingPage}
                />
              )}
            </Box>
            <Text>{dueDate ? `${sendDate} of the month` : <HiddenText visible={displayHiddenText} />}</Text>
          </Box>
        )}
        <Box
          css={{
            paddingRight: 'default',
            paddingTop: 'default',
            paddingBottom: 'default',
          }}
        >
          <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter', minHeight: 'action.xs' }}>
            <Paragraph textStyle="body-bold">Autopay</Paragraph>
            {(renterCanEditPayment || allowAutoPayEdit) && (
              <IconButton
                icon={<IconEditFilled />}
                title="Edit autopay"
                tone="brand"
                emphasis="bare"
                size="xs"
                shape="square"
                onClick={goToEditPaymentFrequencyPage}
              />
            )}
          </Box>
          <Text>{!displayHiddenText ? `${recurring ? 'ON' : 'OFF'}` : <HiddenText visible={displayHiddenText} />}</Text>
        </Box>
        {recordedByName && (
          <Box
            css={{
              paddingRight: 'default',
              paddingTop: 'default',
              paddingBottom: 'default',
            }}
          >
            <Box css={{ display: 'flex', alignItems: 'center', minHeight: 'action.xs' }}>
              <Paragraph textStyle="body-bold">Recorded by</Paragraph>
            </Box>
            <Text css={{ color: 'text.subtle' }}>{recordedByName}</Text>
          </Box>
        )}
        {(selectedPaymentMethod || isCompleted) && (
          <Box
            css={{
              paddingRight: 'default',
              paddingTop: 'default',
              paddingBottom: 'default',
            }}
          >
            <PaymentMethodModule
              accountName={displayAccountName}
              accountLast4={displayAccountLast4}
              onClick={() => goToEditPaymentInfoPage && goToEditPaymentInfoPage(RENT_PAYMENT)}
              editable={renterCanEditPayment}
            />
          </Box>
        )}
        {shouldShowTotalPayment && (
          <Box
            css={{
              paddingRight: 'default',
              paddingTop: 'default',
              paddingBottom: 'default',
            }}
          >
            <Box css={{ display: 'flex', alignItems: 'center', minHeight: 'action.xs' }}>
              <Paragraph textStyle="body-bold">Total payment</Paragraph>
            </Box>
            <Text>{totalPaymentText ? totalPaymentText : <HiddenText visible={displayHiddenText} />}</Text>
          </Box>
        )}
      </Box>
    </>
  );
};
